/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace betaprime
*/
var betaprime = {};

/**
* @name cdf
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/cdf}
*/
import cdf from './../../../../base/dists/betaprime/cdf';
setReadOnly( betaprime, 'cdf', cdf );

/**
* @name BetaPrime
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/ctor}
*/
import BetaPrime from './../../../../base/dists/betaprime/ctor';
setReadOnly( betaprime, 'BetaPrime', BetaPrime );

/**
* @name kurtosis
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/kurtosis}
*/
import kurtosis from './../../../../base/dists/betaprime/kurtosis';
setReadOnly( betaprime, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/logcdf}
*/
import logcdf from './../../../../base/dists/betaprime/logcdf';
setReadOnly( betaprime, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/logpdf}
*/
import logpdf from './../../../../base/dists/betaprime/logpdf';
setReadOnly( betaprime, 'logpdf', logpdf );

/**
* @name mean
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/mean}
*/
import mean from './../../../../base/dists/betaprime/mean';
setReadOnly( betaprime, 'mean', mean );

/**
* @name mode
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/mode}
*/
import mode from './../../../../base/dists/betaprime/mode';
setReadOnly( betaprime, 'mode', mode );

/**
* @name pdf
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/pdf}
*/
import pdf from './../../../../base/dists/betaprime/pdf';
setReadOnly( betaprime, 'pdf', pdf );

/**
* @name quantile
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/quantile}
*/
import quantile from './../../../../base/dists/betaprime/quantile';
setReadOnly( betaprime, 'quantile', quantile );

/**
* @name skewness
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/skewness}
*/
import skewness from './../../../../base/dists/betaprime/skewness';
setReadOnly( betaprime, 'skewness', skewness );

/**
* @name stdev
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/stdev}
*/
import stdev from './../../../../base/dists/betaprime/stdev';
setReadOnly( betaprime, 'stdev', stdev );

/**
* @name variance
* @memberof betaprime
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/betaprime/variance}
*/
import variance from './../../../../base/dists/betaprime/variance';
setReadOnly( betaprime, 'variance', variance );


// EXPORTS //

export default betaprime;
